// Network error handling utilities with retries and fallbacks

interface RetryOptions {
  maxRetries?: number;
  delayMs?: number;
  backoffMultiplier?: number;
  shouldRetry?: (error: any) => boolean;
}

interface NetworkErrorState {
  isOffline: boolean;
  consecutiveFailures: number;
  lastFailureTime: number;
  circuitBreakerOpen: boolean;
}

class NetworkErrorHandler {
  private errorState: NetworkErrorState = {
    isOffline: false,
    consecutiveFailures: 0,
    lastFailureTime: 0,
    circuitBreakerOpen: false
  };

  private readonly CIRCUIT_BREAKER_THRESHOLD = 3;
  private readonly CIRCUIT_BREAKER_TIMEOUT = 30000; // 30 seconds

  async withRetry<T>(
    operation: () => Promise<T>,
    options: RetryOptions = {}
  ): Promise<T> {
    const {
      maxRetries = 3,
      delayMs = 1000,
      backoffMultiplier = 1.5,
      shouldRetry = this.defaultShouldRetry
    } = options;

    // Check circuit breaker
    if (this.errorState.circuitBreakerOpen) {
      const timeSinceLastFailure = Date.now() - this.errorState.lastFailureTime;
      if (timeSinceLastFailure < this.CIRCUIT_BREAKER_TIMEOUT) {
        throw new Error('Circuit breaker is open - too many recent failures');
      } else {
        // Reset circuit breaker
        this.errorState.circuitBreakerOpen = false;
        this.errorState.consecutiveFailures = 0;
      }
    }

    let lastError: any;
    let currentDelay = delayMs;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await operation();
        
        // Success - reset error state
        this.errorState.consecutiveFailures = 0;
        this.errorState.isOffline = false;
        
        return result;
      } catch (error) {
        lastError = error;
        
        // Update error state
        this.errorState.consecutiveFailures++;
        this.errorState.lastFailureTime = Date.now();
        
        // Check if we should open circuit breaker
        if (this.errorState.consecutiveFailures >= this.CIRCUIT_BREAKER_THRESHOLD) {
          this.errorState.circuitBreakerOpen = true;
        }

        // Determine if we should retry
        if (attempt < maxRetries && shouldRetry(error)) {
          console.warn(`Network operation failed (attempt ${attempt + 1}/${maxRetries + 1}):`, error.message);
          
          // Wait before retry with exponential backoff
          await this.delay(currentDelay);
          currentDelay *= backoffMultiplier;
          
          continue;
        }
        
        // No more retries or shouldn't retry
        break;
      }
    }

    // All retries failed
    this.errorState.isOffline = this.isNetworkError(lastError);
    throw lastError;
  }

  private defaultShouldRetry = (error: any): boolean => {
    // Don't retry on authentication errors or client errors (4xx)
    if (error?.status >= 400 && error?.status < 500) {
      return false;
    }
    
    // Retry on network errors, timeouts, and server errors (5xx)
    return this.isNetworkError(error) || 
           error?.status >= 500 || 
           error?.code === 'PGRST301'; // PostgREST timeout
  };

  private isNetworkError(error: any): boolean {
    return error?.code === 'NETWORK_ERROR' ||
           error?.message?.includes('fetch') ||
           error?.message?.includes('network') ||
           !navigator.onLine;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  getErrorState(): NetworkErrorState {
    return { ...this.errorState };
  }

  isOffline(): boolean {
    return this.errorState.isOffline || !navigator.onLine;
  }

  reset(): void {
    this.errorState = {
      isOffline: false,
      consecutiveFailures: 0,
      lastFailureTime: 0,
      circuitBreakerOpen: false
    };
  }
}

export const networkErrorHandler = new NetworkErrorHandler();

// User-friendly error messages
export const getErrorMessage = (error: any): string => {
  if (!navigator.onLine) {
    return 'You appear to be offline. Please check your internet connection.';
  }
  
  if (error?.message?.includes('Circuit breaker')) {
    return 'Service temporarily unavailable due to repeated failures. Please try again in a moment.';
  }
  
  if (error?.status === 429) {
    return 'Too many requests. Please wait a moment before trying again.';
  }
  
  if (error?.status >= 500) {
    return 'Server error. Our team has been notified and is working on a fix.';
  }
  
  if (error?.code === 'PGRST301') {
    return 'Request timeout. This might be due to slow connection or heavy server load.';
  }
  
  return 'Something went wrong loading data. Please try again.';
};

// Fallback data for when network fails
export const getFallbackData = (dataType: string): any => {
  const fallbacks = {
    posts: [],
    casts: [],
    announcements: [
      {
        id: 'offline-1',
        title: 'Offline Mode',
        content: 'You are currently browsing cached content. Connect to internet for latest updates.',
        type: 'info',
        is_active: true,
        start_date: null,
        end_date: null,
        display_type: 'always',
        post_frequency: null,
        created_at: new Date().toISOString()
      }
    ],
    communities: [
      {
        id: 'offline-community',
        name: 'Local Farming Community',
        description: 'Connect when online to see communities in your area',
        members_count: 0,
        growth_percentage: 0,
        plant_hardiness_zone: null,
        'Community Type': 'General'
      }
    ]
  };
  
  return fallbacks[dataType as keyof typeof fallbacks] || [];
};